import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PersonIcon from "@mui/icons-material/Person";
import { toast } from "react-toastify";
import CustomerTransactionsModal from "./CustomerTransactionsModal";
import axiosInstance from "../../../../api/axiosConfig";

function Customers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Customer Modal States
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [openModal, setOpenModal] = useState(false);

  // Pagination States
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 10;

  // Fetch Customers
  const fetchCustomers = useCallback(
    async (currentPage) => {
      try {
        setLoading(true);
        const response = await axiosInstance.get(
          "/marketplace/getequity/get-customers/",
          {
            params: {
              page: currentPage,
              limit: limit,
            },
          },
        );

        if (response.data?.status === "successful") {
          setCustomers(response.data.data || []);
          setTotalPages(response.data.totalPages || 1);
        }
      } catch (error) {
        console.error("Error fetching customers: ", error);
        toast.error(
          error.response?.data?.message || "Failed to load customers.",
        );
      } finally {
        setLoading(false);
      }
    },
    [limit],
  );

  useEffect(() => {
    fetchCustomers(page);
  }, [page, fetchCustomers]);

  // Handle customer row click -> Opens modal
  const handleCustomerClick = (customer) => {
    setSelectedCustomer(customer);
    setOpenModal(true);
  };

  // Client-side search filtering across Name & Email
  const filteredCustomers = customers.filter(
    (customer) =>
      customer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.email?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <Box
      sx={{ p: 3, bgcolor: "#ffffff", color: "#000000", minHeight: "100vh" }}
    >
      {/* Header & Search Bar */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Typography
          variant="h5"
          sx={{ fontWeight: 800, textTransform: "uppercase", letterSpacing: 1 }}
        >
          Customers
        </Typography>

        <TextField
          size="small"
          placeholder="Search by name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#000000" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            width: { xs: "100%", sm: 320 },
            "& .MuiOutlinedInput-root": {
              borderRadius: 0,
              bgcolor: "#ffffff",
              "& fieldset": { borderColor: "#000000", borderWidth: "1px" },
              "&:hover fieldset": { borderColor: "#000000" },
              "&.Mui-focused fieldset": {
                borderColor: "#000000",
                borderWidth: "2px",
              },
            },
          }}
        />
      </Box>

      {/* Customer Table */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          border: "2px solid #000000",
          borderRadius: 0,
          bgcolor: "#ffffff",
        }}
      >
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow sx={{ bgcolor: "#000000" }}>
              <TableCell sx={{ color: "#ffffff", fontWeight: 700, width: 60 }}>
                User
              </TableCell>
              <TableCell sx={{ color: "#ffffff", fontWeight: 700 }}>
                Customer Name
              </TableCell>
              <TableCell sx={{ color: "#ffffff", fontWeight: 700 }}>
                Email Address
              </TableCell>
              <TableCell sx={{ color: "#ffffff", fontWeight: 700 }}>
                Created At
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                  <CircularProgress sx={{ color: "#000000" }} />
                </TableCell>
              </TableRow>
            ) : filteredCustomers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No customers found.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredCustomers.map((customer) => (
                <TableRow
                  key={customer._id || customer.id}
                  onClick={() => handleCustomerClick(customer)}
                  sx={{
                    cursor: "pointer",
                    transition: "background-color 0.15s ease",
                    "&:hover": { bgcolor: "#f0f0f0" },
                    "&:last-child td, &:last-child th": { border: 0 },
                    borderBottom: "1px solid #e0e0e0",
                  }}
                >
                  <TableCell>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: "#000000",
                        color: "#ffffff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <PersonIcon fontSize="small" />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#000000" }}>
                    {customer.name || "N/A"}
                  </TableCell>
                  <TableCell sx={{ color: "#333333" }}>
                    {customer.email || "N/A"}
                  </TableCell>
                  <TableCell sx={{ color: "#666666", fontSize: "0.85rem" }}>
                    {customer.createdAt
                      ? new Date(customer.createdAt).toLocaleDateString(
                          "en-US",
                          {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          },
                        )
                      : "N/A"}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination Controls */}
      {!loading && totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, value) => setPage(value)}
            shape="rounded"
            sx={{
              "& .MuiPaginationItem-root": {
                borderRadius: 0,
                color: "#000000",
                "&.Mui-selected": {
                  bgcolor: "#000000",
                  color: "#ffffff",
                  "&:hover": { bgcolor: "#333333" },
                },
              },
            }}
          />
        </Box>
      )}

      {/* Customer Transactions Popup Modal */}
      {openModal && (
        <CustomerTransactionsModal
          open={openModal}
          onClose={() => setOpenModal(false)}
          customer={selectedCustomer}
        />
      )}
    </Box>
  );
}

export default Customers;
